// components/Navbar.js
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FiMenu, 
  FiBell, 
  FiUser, 
  FiChevronDown,
  FiSun,
  FiMoon,
  FiLogOut,
  FiSettings,
  FiHelpCircle,
  FiSearch
} from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";

const NavbarADM = ({ toggleSidebar, isMobile, tel }) => {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState("");
  const userMenuRef = useRef(null);
  const notificationsRef = useRef(null);

  const [notifications, setNotifications] = useState([
    { id: 1, text: "Nouveau gagnant ajouté", time: "Il y a 5 min", read: false, icon: "🎉" },
    { id: 2, text: "Mise à jour du système", time: "Il y a 1 heure", read: false, icon: "🔄" },
    { id: 3, text: "Rapport mensuel disponible", time: "Il y a 2 heures", read: true, icon: "📊" },
    { id: 4, text: "Nouveau message reçu", time: "Il y a 3 heures", read: true, icon: "💬" }
  ]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      clearInterval(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAsRead = (id) => {
    setNotifications(notifications.map(n => 
      n.id === id ? { ...n, read: true } : n
    ));
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, read: true })));
  };

  const handleLogout = () => {
    localStorage.removeItem('login2');
    localStorage.removeItem('userId');
    navigate('/login');
  };

  // Styles CSS intégrés
  const styles = `
    /* Navbar Principal */
    .navbar {
      background: rgba(15, 25, 55, 0.75);
      backdrop-filter: blur(16px);
      border-bottom: 1px solid rgba(100, 150, 255, 0.2);
      padding: 12px 24px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      position: sticky;
      top: 0;
      z-index: 100;
      transition: all 0.3s ease;
    }

    .navbar-left {
      display: flex;
      align-items: center;
      gap: 20px;
    }

    /* Bouton menu toggle */
    .menu-toggle {
      background: rgba(0, 100, 255, 0.15);
      border: 1px solid rgba(0, 180, 255, 0.3);
      border-radius: 10px;
      padding: 8px 12px;
      cursor: pointer;
      color: rgba(170, 200, 255, 0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      font-size: 20px;
    }

    .menu-toggle:hover {
      background: rgba(0, 100, 255, 0.25);
      color: white;
      transform: scale(1.05);
    }

    /* Titre */
    .navbar-left h1 {
      font-size: 20px;
      font-weight: 600;
      background: linear-gradient(135deg, #ffffff, #aac8ff);
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
      margin: 0;
      letter-spacing: -0.3px;
    }

    /* Barre de recherche */
    .search-bar {
      position: relative;
      margin-left: 20px;
    }

    .search-input {
      background: rgba(10, 20, 45, 0.6);
      border: 1px solid rgba(100, 150, 255, 0.2);
      border-radius: 10px;
      padding: 8px 12px 8px 36px;
      color: white;
      font-size: 14px;
      width: 250px;
      transition: all 0.3s ease;
    }

    .search-input:focus {
      outline: none;
      border-color: rgba(0, 180, 255, 0.6);
      background: rgba(10, 20, 45, 0.8);
      box-shadow: 0 0 10px rgba(0, 180, 255, 0.2);
      width: 300px;
    }

    .search-input::placeholder {
      color: rgba(170, 200, 255, 0.4);
    }

    .search-icon {
      position: absolute;
      left: 10px;
      top: 50%;
      transform: translateY(-50%);
      color: rgba(170, 200, 255, 0.6);
      font-size: 16px;
    }

    /* Partie droite */
    .navbar-right {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    /* Date et heure */
    .datetime {
      text-align: right;
      margin-right: 8px;
    }

    .current-time {
      font-size: 14px;
      font-weight: 600;
      color: rgba(170, 200, 255, 0.9);
      font-family: monospace;
    }

    .current-date {
      font-size: 10px;
      color: rgba(170, 200, 255, 0.5);
    }

    /* Boutons d'action */
    .action-btn {
      background: rgba(0, 100, 255, 0.15);
      border: 1px solid rgba(0, 180, 255, 0.3);
      border-radius: 10px;
      padding: 8px;
      cursor: pointer;
      color: rgba(170, 200, 255, 0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      position: relative;
    }

    .action-btn:hover {
      background: rgba(0, 100, 255, 0.25);
      color: white;
      transform: translateY(-2px);
    }

    /* Badge de notification */
    .notification-badge {
      position: absolute;
      top: -5px;
      right: -5px;
      background: linear-gradient(135deg, #ff4444, #ff6b6b);
      color: white;
      font-size: 9px;
      padding: 2px 5px;
      border-radius: 10px;
      min-width: 16px;
      text-align: center;
      font-weight: 600;
      animation: pulse 2s ease-in-out infinite;
    }

    @keyframes pulse {
      0%, 100% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.1);
      }
    }

    /* Profil utilisateur */
    .user-profile {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      padding: 6px 12px;
      background: rgba(0, 100, 255, 0.1);
      border: 1px solid rgba(0, 180, 255, 0.2);
      border-radius: 12px;
      transition: all 0.3s ease;
    }

    .user-profile:hover {
      background: rgba(0, 100, 255, 0.2);
      border-color: rgba(0, 180, 255, 0.4);
    }

    .user-avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: linear-gradient(135deg, #00b4ff, #0066ff);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .user-info-nav {
      display: flex;
      flex-direction: column;
    }

    .user-name-nav {
      font-size: 13px;
      font-weight: 600;
      color: white;
    }

    .user-role-nav {
      font-size: 10px;
      color: rgba(170, 200, 255, 0.7);
    }

    /* Dropdown menus */
    .dropdown-menu {
      position: absolute;
      top: 100%;
      right: 0;
      margin-top: 10px;
      background: rgba(15, 25, 55, 0.95);
      backdrop-filter: blur(16px);
      border: 1px solid rgba(100, 150, 255, 0.2);
      border-radius: 12px;
      min-width: 280px;
      overflow: hidden;
      z-index: 1000;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    }

    /* Notifications */
    .notifications-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 16px;
      border-bottom: 1px solid rgba(100, 150, 255, 0.15);
    }

    .notifications-title {
      font-size: 14px;
      font-weight: 600;
      color: white;
    }

    .mark-all-btn {
      font-size: 11px;
      color: #00b4ff;
      background: none;
      border: none;
      cursor: pointer;
      transition: color 0.3s ease;
    }

    .mark-all-btn:hover {
      color: #00d4ff;
    }

    .notifications-list {
      max-height: 350px;
      overflow-y: auto;
    }

    .notifications-list::-webkit-scrollbar {
      width: 4px;
    }

    .notifications-list::-webkit-scrollbar-track {
      background: rgba(100, 150, 255, 0.1);
    }

    .notifications-list::-webkit-scrollbar-thumb {
      background: rgba(0, 180, 255, 0.4);
    }

    .notification-item {
      padding: 12px 16px;
      border-bottom: 1px solid rgba(100, 150, 255, 0.1);
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      gap: 12px;
    }

    .notification-item:hover {
      background: rgba(0, 100, 255, 0.1);
    }

    .notification-item.unread {
      background: rgba(0, 180, 255, 0.05);
    }

    .notification-icon {
      font-size: 20px;
    }

    .notification-content {
      flex: 1;
    }

    .notification-text {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.9);
      margin-bottom: 4px;
    }

    .notification-time {
      font-size: 10px;
      color: rgba(170, 200, 255, 0.6);
    }

    /* Menu utilisateur */
    .user-menu {
      min-width: 200px;
    }

    .user-menu-header {
      padding: 16px;
      text-align: center;
      border-bottom: 1px solid rgba(100, 150, 255, 0.15);
    }

    .user-menu-avatar {
      width: 50px;
      height: 50px;
      border-radius: 50%;
      background: linear-gradient(135deg, #00b4ff, #0066ff);
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 10px;
    }

    .user-menu-name {
      font-size: 14px;
      font-weight: 600;
      color: white;
    }

    .user-menu-email {
      font-size: 11px;
      color: rgba(170, 200, 255, 0.7);
    }

    .menu-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      color: rgba(170, 200, 255, 0.8);
      text-decoration: none;
      transition: all 0.3s ease;
      cursor: pointer;
    }

    .menu-item:hover {
      background: rgba(0, 100, 255, 0.1);
      color: white;
      padding-left: 20px;
    }

    .menu-divider {
      height: 1px;
      background: rgba(100, 150, 255, 0.1);
      margin: 8px 0;
    }

    /* Animations */
    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    /* Responsive */
    @media (max-width: 768px) {
      .navbar {
        padding: 12px 16px;
      }

      .navbar-left h1 {
        font-size: 16px;
      }

      .search-bar {
        display: none;
      }

      .datetime {
        display: none;
      }

      .user-info-nav {
        display: none;
      }

      .user-profile {
        padding: 6px;
      }

      .dropdown-menu {
        position: fixed;
        top: auto;
        right: 16px;
        left: 16px;
        width: auto;
        max-width: none;
      }
    }

    @media (max-width: 480px) {
      .navbar-left h1 {
        font-size: 14px;
      }
    }
  `;

  return (
    <>
      <style>{styles}</style>
      
      <div className="navbar">
        <div className="navbar-left">
          {isMobile && (
            <motion.button 
              className="menu-toggle" 
              onClick={toggleSidebar}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiMenu size={20} />
            </motion.button>
          )}
          <motion.h1
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            Revolution Fiscale en RDC
          </motion.h1>
          
          {/* Barre de recherche pour desktop */}
          <div className="search-bar">
            <FiSearch className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="navbar-right">
          {/* Date et heure */}
          <motion.div 
            className="datetime"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="current-time">
              {currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </div>
            <div className="current-date">
              {currentTime.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
          </motion.div>

          {/* Bouton thème */}
        
   

          {/* Profil utilisateur */}
          <div style={{ position: 'relative' }} ref={userMenuRef}>
            <motion.div 
              className="user-profile"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <div className="user-avatar">
                <FiUser size={16} color="white" />
              </div>
              <div className="user-info-nav">
                <span className="user-name-nav">REFISC</span>
                <span className="user-role-nav">{tel}</span>
              </div>
              <FiChevronDown size={14} />
            </motion.div>

            <AnimatePresence>
              {showUserMenu && (
                <motion.div 
                  className="dropdown-menu user-menu"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="user-menu-header">
                    <div className="user-menu-avatar">
                      <FiUser size={24} color="white" />
                    </div>
                    <div className="user-menu-name">REFISC</div>
                   
                  </div>
                  <Link to="/profile" className="menu-item" onClick={() => setShowUserMenu(false)}>
                    <FiUser size={16} />
                    <span>Mon profil</span>
                  </Link>
                  <Link to="/settings" className="menu-item" onClick={() => setShowUserMenu(false)}>
                    <FiSettings size={16} />
                    <span>Paramètres</span>
                  </Link>
                 
                  <div className="menu-divider"></div>
                  <div className="menu-item" onClick={handleLogout}>
                    <FiLogOut size={16} />
                    <span>Déconnexion</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </>
  );
};

export default NavbarADM;